import { customElement, property, state } from "lit/decorators.js";
import type { PropertyValues } from "lit";
import type { EditorView } from "@codemirror/view";
import * as Sentry from "@sentry/browser";
import { DodonaElement } from "@/components/meta/dodona_element";
import type { FeedbackReadyDetail } from "@/components/feedback/feedback_table";
import type { HandinAnnouncer } from "@/components/handin_announcer";
import { configureEditor, setCode } from "@/editor";
import { initTooltips, updateURLParameter, fetch, ResponseError, setHTMLExecuteScripts } from "@/utilities";
import { Toast } from "@/toast";
import { submissionState } from "@/state/Submissions";
import type { HandinProgress } from "@/handin_progress";
import { HandinJudging } from "@/handin_judging";
import { resultMessage, resultReadyMessage, SubmissionStatus } from "@/submission_status";
import type { ActivityStatusChange } from "@/components/activity_series";
import { calloutHtml } from "@/callout";
import { I18n } from "@/i18n/i18n";
import { localStore } from "@/storage";
import { prefersReducedMotion } from "@/motion";
import { celebrateSuccess, getPositiveEmoji, loadCelebration } from "@/celebration";
import type { CompositeAnswer } from "@/iframe_messages";

type SubmissionHistoryEl = HTMLElement & {
    activeId: number | null;
    submissions: SubmissionStatus[];
    readonly accepted: boolean;
    readonly solved: boolean;
    numberFor(id: number | null): number | null;
};
type SubmissionBarEl = HTMLElement & { submission: SubmissionStatus | null; showBack: boolean };
type ResultNoticeEl = HTMLElement & { submission: SubmissionStatus | null };
type HandinTitleEl = HTMLElement & {
    submission: SubmissionStatus | null;
    celebrate(submission: SubmissionStatus, icon: string): void;
};
type HandinProgressTarget = HTMLElement & { progress: HandinProgress };
/** What the card shows: the editor, or the feedback of a submission. */
type HandinView = { kind: "editor" } | { kind: "submission"; id: number };

/**
 * Wraps the hand-in card of an exercise: the editor, handing in through HandinJudging, showing
 * feedback and switching between the editor and a submission. Which of the two is shown, and a
 * judged submission the user has yet to open, are state of the card. On each update it shows them
 * in its panes and in the d-handin-title, d-submission-bar, d-submission-history and
 * d-result-notice inside it, which display them. It also sets the progress of the hand-in on
 * d-handin-button and d-judging-overlay, which render from it, and on d-handin-announcer, which
 * speaks it. Once the d-feedback-table of a judged submission sends `feedback-ready`, its verdict
 * becomes part of the name of `#submission-wrapper` through aria-labelledby (NVDA does not speak a
 * region's description). Moving focus to the region speaks it; when the region already has focus,
 * d-handin-announcer says it instead.
 *
 * A submission that finishes judging is shown in place when the editor is shown, and once its
 * feedback is ready, the page scrolls the card to its top. When a submission is shown,
 * d-result-notice offers the result and d-handin-announcer says it is ready.
 *
 * The editor is shown when the card contains `#editor-text`. The answers of a composite exercise
 * come from the composite-register and composite-answer events of d-description-frame. They are
 * handed in with `#composite-submit-btn`, which is disabled while a hand-in is in progress.
 *
 * Sends `latest-submission-changed` on the document with the status of a new submission, once
 * when it is created and again when it is judged.
 *
 * Renders no template of its own.
 *
 * @element d-handin-card
 *
 * @prop {number} exerciseId
 * @prop {number} courseId
 * @prop {number} seriesId
 * @prop {string} programmingLanguage - Lexer name for the editor.
 * @prop {string} naturalLanguage - Language of the description, which the judge uses for its feedback.
 * @prop {string} submissionsUrl - Base url of a submission, to which its id is appended.
 * @prop {string} fromLocalStorage - Key of code in local storage to start the editor with.
 * @prop {string} boilerplate - Code the restore button puts back in the editor.
 * @prop {boolean} loggedIn
 * @prop {boolean} readOnly - Whether the editor only shows the last submission.
 */
@customElement("d-handin-card")
export class HandinCard extends DodonaElement {
    @property({ type: Number, attribute: "exercise-id" })
    exerciseId: number;
    @property({ type: Number, attribute: "course-id" })
    courseId: number | undefined;
    @property({ type: Number, attribute: "series-id" })
    seriesId: number | undefined;
    @property({ type: String, attribute: "programming-language" })
    programmingLanguage: string;
    @property({ type: String, attribute: "natural-language" })
    naturalLanguage: string;
    @property({ type: String, attribute: "submissions-url" })
    submissionsUrl: string;
    @property({ type: String, attribute: "from-local-storage" })
    fromLocalStorage: string | undefined;
    @property({ type: String })
    boilerplate = "";
    @property({ type: Boolean, attribute: "logged-in" })
    loggedIn = false;
    @property({ type: Boolean, attribute: "read-only" })
    readOnly = false;

    @state()
    private view: HandinView = { kind: "editor" };
    // A judged submission whose result the user has yet to open, which d-result-notice offers.
    @state()
    private pendingResult: SubmissionStatus | null = null;

    private _editor: EditorView | undefined;
    private readonly judging = new HandinJudging(this);
    private initialized = false;
    private editorShown = false;
    private submitted = false;
    private loadedCode = "";
    // Aborted by the next feedback load, which replaces what this one would show.
    private feedbackRequest: AbortController | null = null;
    // The judged submission that took the place of the editor, until its feedback is ready.
    private revealedResultId: number | null = null;
    private readonly compositeAnswers: CompositeAnswer[] = [];

    /** The CodeMirror editor, once it has mounted. */
    get editor(): EditorView | undefined {
        return this._editor;
    }

    connectedCallback(): void {
        super.connectedCallback();
        // The description frame is not inside the card, so its events only meet at the document.
        document.addEventListener("composite-register", this.onCompositeRegister);
        document.addEventListener("composite-answer", this.onCompositeAnswer);
        if (!this.initialized) {
            this.initialized = true;
            void this.init();
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        document.removeEventListener("composite-register", this.onCompositeRegister);
        document.removeEventListener("composite-answer", this.onCompositeAnswer);
    }

    // The judging controller asks for an update whenever the progress or the status of its submission changes.
    updated(changed: PropertyValues): void {
        super.updated(changed);
        const { progress, submission } = this.judging;
        // Lit keeps a property set before the element upgrades, so this does not depend on upgrade order.
        for (const el of this.querySelectorAll<HandinProgressTarget>(
            "d-handin-button, d-judging-overlay, d-handin-announcer",
        )) {
            el.progress = progress;
        }
        this.querySelector<HTMLButtonElement>("#composite-submit-btn")?.toggleAttribute("disabled", this.judging.busy);
        if (submission) this.updateInSubmissionHistory(submission);
        // The server renders the editor, where `view` starts, so its first value is already on the page.
        if (changed.get("view") !== undefined) this.applyView();
        if (changed.has("pendingResult")) {
            const noticeEl = this.resultNotice;
            if (noticeEl) noticeEl.submission = this.pendingResult;
        }
    }

    /** Puts code from elsewhere, such as the sandbox, in the editor and shows the editor. */
    setEditorCode(code: string): void {
        setCode(this._editor, code);
        this.showEditor();
    }

    /** Scrolls the card into view and moves focus to the editor. */
    revealEditor(): void {
        this.focusEditor();
        this.querySelector(".handin-card")?.scrollIntoView({
            block: "nearest",
            behavior: prefersReducedMotion() ? "instant" : "smooth",
        });
    }

    // In Safari, CodeMirror focuses without preventScroll and then scrolls the page back to where it was. The smooth
    // scroll-behavior of the page would turn that into an animation back, which outlasts any scroll that follows.
    private focusEditor(): void {
        const root = document.documentElement;
        const scrollBehavior = root.style.scrollBehavior;
        root.style.scrollBehavior = "auto";
        try {
            this._editor?.focus();
        } finally {
            root.style.scrollBehavior = scrollBehavior;
        }
    }

    private get announcer(): HandinAnnouncer | null {
        return this.querySelector<HandinAnnouncer>("d-handin-announcer");
    }

    private get handinButton(): HTMLElement | null {
        return this.querySelector<HTMLElement>("d-handin-button");
    }

    private get submissionHistory(): SubmissionHistoryEl | null {
        return this.querySelector<SubmissionHistoryEl>("d-submission-history");
    }

    private get submissionBar(): SubmissionBarEl | null {
        return this.querySelector<SubmissionBarEl>("d-submission-bar");
    }

    private get resultNotice(): ResultNoticeEl | null {
        return this.querySelector<ResultNoticeEl>("d-result-notice");
    }

    private get handinTitle(): HandinTitleEl | null {
        return this.querySelector<HandinTitleEl>("d-handin-title");
    }

    private get submissionWrapper(): HTMLElement | null {
        return this.querySelector<HTMLElement>("#submission-wrapper");
    }

    private get submissionVerdict(): HTMLElement | null {
        return this.querySelector<HTMLElement>("#submission-verdict");
    }

    /** The id of the submission being shown, or null while the editor is. */
    private get shownId(): number | null {
        return this.view.kind === "submission" ? this.view.id : null;
    }

    /** The submission being shown, as the history has it, or null while the editor is. */
    private get shownSubmission(): SubmissionStatus | null {
        const id = this.shownId;
        if (id === null) return null;
        return this.submissionHistory?.submissions.find((s) => s.id === id) ?? null;
    }

    private async init(): Promise<void> {
        const editorText = this.querySelector<HTMLElement>("#editor-text");
        this.editorShown = editorText !== null;
        if (this.editorShown) {
            await this.initEditor(editorText);
            this.initRestoreBoilerplateButton();
        }

        this.handinButton?.addEventListener("handin", () => void this.submit());
        this.querySelector("#composite-submit-btn")?.addEventListener("click", () => void this.submitComposite());
        this.querySelector("#submission-copy-btn")?.addEventListener("click", () => void this.editShownSubmission());

        const bar = this.submissionBar;
        if (bar) {
            bar.addEventListener("edit", () => this.editShownSubmission());
            bar.addEventListener("back", () => this.showEditor());
        }

        this.submissionHistory?.addEventListener("submission-selected", (e: Event) => {
            const { id } = (e as CustomEvent<{ id: number }>).detail;
            if (this.declinesLosingUnsubmittedCode()) return;
            const toggleEl = this.querySelector<HTMLElement>("[aria-controls='submission-history-dropdown']");
            if (toggleEl) {
                toggleEl.addEventListener("hidden.bs.dropdown", () => toggleEl.blur(), { once: true });
                bootstrap.Dropdown.getInstance(toggleEl)?.hide();
            }
            this.showSubmission(id);
        });

        this.submissionWrapper?.addEventListener("feedback-ready", (e: Event) =>
            this.onFeedbackReady((e as CustomEvent<FeedbackReadyDetail>).detail),
        );

        this.resultNotice?.addEventListener("view-result", () => {
            if (this.declinesLosingUnsubmittedCode()) return;
            this.showSubmission(this.pendingResult.id);
        });
    }

    private async initEditor(editorText: HTMLElement): Promise<void> {
        this._editor = await configureEditor(editorText, this.programmingLanguage, this.readOnly);
        // Reveal the editor only once CodeMirror has mounted, so the raw,
        // un-highlighted source is never briefly visible during page load.
        editorText.classList.remove("editor-loading");

        if (this.fromLocalStorage) {
            const code = localStore.get(this.fromLocalStorage);
            if (code) {
                setCode(this._editor, code);
            }
        }

        this.loadedCode = this._editor.state.doc.toString();
    }

    private initRestoreBoilerplateButton(): void {
        const restoreWarning = this.querySelector<HTMLElement>("#restore-boilerplate");
        if (!restoreWarning) return;

        restoreWarning.querySelector("button").addEventListener("click", () => {
            this.loadCode(this.boilerplate);
            this._editor.focus();
            restoreWarning.hidden = true;
        });
    }

    private onCompositeRegister = (event: CustomEvent<{ id: string }>): void => {
        const { id } = event.detail;
        if (!this.compositeAnswers.some((a) => a.id === id)) {
            this.compositeAnswers.push({ id, value: "" });
        }
    };

    private onCompositeAnswer = (event: CustomEvent<CompositeAnswer>): void => {
        const answer = this.compositeAnswers.find((a) => a.id === event.detail.id);
        if (answer) answer.value = event.detail.value;
    };

    /** Fills the editor, taking what it now holds as the baseline for `hasUnsubmittedCode`. */
    private loadCode(code: string): void {
        setCode(this._editor, code);
        this.loadedCode = code;
    }

    private hasUnsubmittedCode(): boolean {
        return this._editor !== undefined && this._editor.state.doc.toString() !== this.loadedCode;
    }

    // Handing in fills the editor with what became the last submission, so from then on editing
    // that submission is how you get back to your own code.
    private canReturnToEditor(): boolean {
        return this.editorShown && !this.submitted;
    }

    // Leaving the editor discards nothing, but the code only stays reachable through "back to
    // editor"; once that is gone, opening a submission needs the user to let go of their changes.
    private declinesLosingUnsubmittedCode(): boolean {
        return (
            this.view.kind === "editor" &&
            !this.canReturnToEditor() &&
            this.hasUnsubmittedCode() &&
            !confirm(I18n.t("js.handin.unsubmitted_code"))
        );
    }

    private editShownSubmission(): void {
        this.loadCode(submissionState.code);
        this.setEditorSource(this.shownId);
        this.showEditor();
    }

    private showEditor(): void {
        // The editor owns the card now, so a late answer for the submission must show nothing and say nothing.
        // Reopening a submission starts a new load anyway.
        this.feedbackRequest?.abort();
        this.view = { kind: "editor" };
    }

    // Shows `view` in the panes, in the footer and in the parts of the card that name what is shown.
    private applyView(): void {
        const isEditor = this.view.kind === "editor";
        const submission = this.shownSubmission;
        this.querySelector<HTMLElement>("#editor-view").hidden = !isEditor;
        this.submissionWrapper.hidden = isEditor;
        const handinBtnEl = this.handinButton;
        if (handinBtnEl) handinBtnEl.hidden = !isEditor;
        const copyBtn = this.querySelector<HTMLButtonElement>("#submission-copy-btn");
        if (copyBtn) copyBtn.hidden = isEditor;
        const sandboxBtnWrapper = this.querySelector("#scratchpad-show-btn")?.parentElement;
        if (sandboxBtnWrapper) sandboxBtnWrapper.hidden = !isEditor;
        const historyEl = this.submissionHistory;
        if (historyEl) historyEl.activeId = this.shownId;
        const titleEl = this.handinTitle;
        if (titleEl) titleEl.submission = submission;
        const barEl = this.submissionBar;
        if (barEl) {
            barEl.showBack = this.canReturnToEditor();
            barEl.submission = submission;
            barEl.hidden = submission === null;
        }
        const hintText = this.querySelector<HTMLElement>("#handin-status-hint-text");
        if (hintText) {
            hintText.textContent = isEditor ? I18n.t("js.handin.hint_editor") : I18n.t("js.handin.hint_result");
        }
        if (isEditor) {
            this._editor?.requestMeasure();
            // The control that switched views is hidden now, which would drop focus to the page start.
            this.focusEditor();
        }
    }

    private celebrateStatus(submission: SubmissionStatus): void {
        const correct = submission.status === "correct";
        if (correct) {
            void celebrateSuccess();
        }
        this.handinTitle?.celebrate(submission, correct ? getPositiveEmoji() : "mdi-emoticon-sad-outline");
    }

    private async submit(): Promise<void> {
        if (!this.loggedIn) return;
        await this.handIn(this._editor.state.doc.toString());
    }

    private async submitComposite(): Promise<void> {
        if (!this.loggedIn) return;
        await this.handIn(this.compositeAnswers.map((a) => a.value).join("\n\n"));
    }

    private async handIn(code: string): Promise<void> {
        if (this.judging.busy) return;
        // Fetch the confetti while the submission is judged.
        void loadCelebration();
        const accepted = await this.judging.handIn(code);
        // Compared to false, since TypeScript without strictNullChecks does not narrow on `!accepted.ok`.
        if (accepted.ok === false) {
            this.showRejection(accepted.message);
            return;
        }
        this.submitted = true;
        this.loadedCode = code;
        this.pendingResult = null;
        this.addToSubmissionHistory(accepted.submission);
        this.sendLatestSubmission("queued");
        new Toast(I18n.t("js.submission-saved"));

        const judged = await this.judging.judge(accepted.submission);
        if (judged.ok === false) {
            this.showLoss(judged.message);
            return;
        }
        this.updateInSubmissionHistory(judged.submission);
        this.sendActivityStatus(judged.submission, accepted.seriesId);
        this.sendLatestSubmission(judged.submission.status);
        await this.onEvaluationComplete(judged.submission);
    }

    private showRejection(message: string): void {
        // A composite exercise has no editor to show the error above.
        if (this.editorShown) {
            this.showSubmissionError(message);
        } else {
            new Toast(message);
        }
        this.announcer?.alert(message);
    }

    private showLoss(message: string): void {
        // The user may be viewing a submission, which hides the editor and the alert above it.
        new Toast(message);
        this.announcer?.alert(message);
    }

    private showSubmission(id: number): void {
        this.view = { kind: "submission", id };
        if (this.pendingResult?.id === id) {
            // The celebration deferred when this result stayed unopened, fired now that the
            // user is looking at the submission it belongs to.
            this.celebrateStatus(this.pendingResult);
            this.pendingResult = null;
        }
        void this.loadFeedback(id);
    }

    /**
     * Names the submission the editor's code was copied from; the server renders this on load.
     * An unknown submission hides the callout, since whatever it said no longer describes the editor.
     */
    private setEditorSource(submissionId: number | null): void {
        const callout = this.querySelector<HTMLElement>("#restore-boilerplate");
        const text = this.querySelector("#editor-source-text");
        if (!callout || !text) return;

        const number = this.submissionHistory?.numberFor(submissionId) ?? null;
        callout.hidden = number === null;
        if (number !== null) {
            text.textContent = I18n.t("js.handin.editing_copy", { number: number });
        }
    }

    private addToSubmissionHistory(submission: SubmissionStatus): void {
        const historyEl = this.submissionHistory;
        if (!historyEl) return;
        historyEl.submissions = [submission, ...historyEl.submissions];
        const countEl = this.querySelector(".submission-history-count");
        if (countEl) {
            countEl.textContent = I18n.t("js.submission_count", { smart_count: historyEl.submissions.length });
        }
    }

    private updateInSubmissionHistory(submission: SubmissionStatus): void {
        const historyEl = this.submissionHistory;
        if (!historyEl) return;
        historyEl.submissions = historyEl.submissions.map((s) => (s.id === submission.id ? submission : s));
    }

    private async loadFeedback(submissionId: number): Promise<void> {
        this.feedbackRequest?.abort();
        const request = new AbortController();
        this.feedbackRequest = request;
        const wrapper = this.submissionWrapper;
        const currentHeight = wrapper.hidden
            ? (this.querySelector<HTMLElement>("#editor-view")?.offsetHeight ?? 0)
            : wrapper.offsetHeight;
        wrapper.innerHTML = `<center><i class="mdi mdi-loading mdi-spin"></i></center>`;
        this.submissionVerdict.textContent = "";
        if (currentHeight > 0) wrapper.style.minHeight = `${currentHeight}px`;
        let feedbackUrl = updateURLParameter(this.submissionsUrl + submissionId, "format", "js");
        feedbackUrl = updateURLParameter(feedbackUrl, "hide_header", "true");
        try {
            const resp = await fetch(feedbackUrl, {
                headers: {
                    accept: "text/javascript",
                },
                signal: request.signal,
            });
            const data = await resp.text();
            setHTMLExecuteScripts(wrapper, data);
            // A feedback table renders its tests later, so the card keeps its height until the table is ready.
            if (!wrapper.querySelector("d-feedback-table")) wrapper.style.minHeight = "";
        } catch (error) {
            // A newer load aborted this one and owns the wrapper now.
            if (request.signal.aborted) return;
            wrapper.style.minHeight = "";
            wrapper.innerHTML = calloutHtml({
                type: "danger",
                icon: "alert-circle-outline",
                message: I18n.t("js.unknown-error-loading-feedback"),
            });
            this.focusFeedback();
            this.announcer?.alert(I18n.t("js.unknown-error-loading-feedback"));
            if (!(error instanceof ResponseError)) throw error;
            return;
        }
        initTooltips();
    }

    // A new load empties the wrapper, so only the latest load's table gets here. Switching to the editor hides that
    // table without emptying the wrapper, and a hidden table must not take focus or set the verdict.
    private onFeedbackReady({ submissionId, counts }: FeedbackReadyDetail): void {
        if (this.shownId !== submissionId) return;
        this.submissionWrapper.style.minHeight = "";
        const shown = this.shownSubmission;
        const verdict = shown ? resultMessage(shown, counts) : "";
        // The verdict is part of the region's name, set before the focus move so it is read with the focus announcement.
        // A polite message in the same accessibility update as the focus move is sometimes dropped by VoiceOver.
        this.submissionVerdict.textContent = verdict;
        const reveal = this.revealedResultId === submissionId;
        this.revealedResultId = null;
        this.focusFeedback(verdict, reveal);
        if (reveal) {
            this.querySelector(".handin-card")?.scrollIntoView({
                block: "start",
                behavior: prefersReducedMotion() ? "instant" : "smooth",
            });
        }
    }

    private focusFeedback(verdict = "", preventScroll = false): void {
        const wrapper = this.submissionWrapper;
        // Showing the result hid whatever had focus. Anything the user focused since is left alone.
        if (document.activeElement === document.body) {
            wrapper.focus({ preventScroll });
        } else if (document.activeElement === wrapper && verdict) {
            // Safari does not move focus on a row click, so the region still has it from the submission shown before.
            this.announcer?.say(verdict);
        }
    }

    // The series views on the page show the activity's state with the same rule as the history.
    private sendActivityStatus(submission: SubmissionStatus, seriesId: number | null): void {
        const historyEl = this.submissionHistory;
        if (!historyEl) return;
        const state = historyEl.accepted ? "accepted" : historyEl.solved ? "regressed" : "started";
        const change: ActivityStatusChange = {
            activityId: this.exerciseId,
            seriesId: seriesId,
            state: state,
            at: submission.created_at,
            accepted: submission.accepted,
            submissionCount: historyEl.submissions.length,
            lastSubmissionUrl: `${this.submissionsUrl}${submission.id}`,
        };
        document.dispatchEvent(new CustomEvent("activity-status-changed", { detail: change }));
    }

    private sendLatestSubmission(status: string): void {
        document.dispatchEvent(new CustomEvent("latest-submission-changed", { detail: { status } }));
    }

    private async onEvaluationComplete(judged: SubmissionStatus): Promise<void> {
        try {
            if (this.view.kind === "submission") {
                // The user is looking at another submission, so announce the result instead of
                // taking the view away from it. The celebration waits until they open it.
                this.pendingResult = judged;
                this.announcer?.say(resultReadyMessage(judged.status));
            } else {
                // From the editor, the user was waiting for this result, so it takes the editor's place.
                this.view = { kind: "submission", id: judged.id };
                this.revealedResultId = judged.id;
                this.celebrateStatus(judged);
                await this.loadFeedback(judged.id);
            }
            new Toast(I18n.t("js.submission-processed"));
        } catch (error) {
            Sentry.captureException(error);
        }
    }

    private showSubmissionError(message: string): void {
        const alert = document.createElement("div");
        alert.classList.add("alert", "alert-danger", "alert-dismissible");
        alert.innerHTML = `<button type="button" class="btn-close btn-close-white" data-bs-dismiss="alert"></button>${message}`;

        const editorWindow = this.querySelector("#editor-window");
        editorWindow.parentElement.insertBefore(alert, editorWindow);
    }
}
